// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/**
 * Fondo de la aplicación — campo de degradados por paleta.
 *
 * Antes había una rejilla fija en \`body::before\` (ver \`design-system.css\`) que
 * se veía en todas las pantallas y competía con el contenido. Ahora el fondo es
 * un campo de tres manchas radiales muy difuminadas que cambian de tono con la
 * paleta activa (\`data-theme\`) y, opcionalmente, se mueven despacio.
 *
 * Lo controla \`theme/background.js\` escribiendo dos atributos en <html>:
 *
 *   data-bg        → aurora (por defecto) | mesh (rejilla antigua) | plain
 *   data-bg-motion → off | soft (por defecto) | lively
 *
 * Sin \`<html data-bg>\` no se pinta nada raro: se asume \`aurora\`.
 */

/* ── Tonos del campo, uno por paleta ─────────────────────────── */

:root,
[data-theme="dark"] {
  --bg-blob-1: rgba(99, 102, 241, .42);
  --bg-blob-2: rgba(139, 92, 246, .30);
  --bg-blob-3: rgba(34, 211, 238, .22);
}

[data-theme="midnight"] {
  --bg-blob-1: rgba(37, 99, 235, .45);
  --bg-blob-2: rgba(99, 102, 241, .30);
  --bg-blob-3: rgba(56, 189, 248, .20);
}

[data-theme="ocean"] {
  --bg-blob-1: rgba(6, 182, 212, .40);
  --bg-blob-2: rgba(59, 130, 246, .28);
  --bg-blob-3: rgba(16, 185, 129, .22);
}

[data-theme="aurora"] {
  --bg-blob-1: rgba(16, 185, 129, .42);
  --bg-blob-2: rgba(45, 212, 191, .28);
  --bg-blob-3: rgba(132, 204, 22, .16);
}

[data-theme="dusk"] {
  --bg-blob-1: rgba(236, 72, 153, .42);
  --bg-blob-2: rgba(168, 85, 247, .30);
  --bg-blob-3: rgba(244, 63, 94, .20);
}

[data-theme="sunset"] {
  --bg-blob-1: rgba(245, 158, 11, .40);
  --bg-blob-2: rgba(249, 115, 22, .28);
  --bg-blob-3: rgba(236, 72, 153, .18);
}

/* Paletas claras: el mismo dibujo, mucho más suave para no ensuciar el blanco. */

[data-theme="light"] {
  --bg-blob-1: rgba(99, 102, 241, .26);
  --bg-blob-2: rgba(139, 92, 246, .18);
  --bg-blob-3: rgba(56, 189, 248, .16);
}

[data-theme="light-sky"] {
  --bg-blob-1: rgba(2, 132, 199, .24);
  --bg-blob-2: rgba(99, 102, 241, .16);
  --bg-blob-3: rgba(6, 182, 212, .16);
}

[data-theme="light-teal"] {
  --bg-blob-1: rgba(13, 148, 136, .24);
  --bg-blob-2: rgba(16, 185, 129, .16);
  --bg-blob-3: rgba(14, 165, 233, .14);
}

[data-theme="light-rose"] {
  --bg-blob-1: rgba(225, 29, 72, .22);
  --bg-blob-2: rgba(236, 72, 153, .16);
  --bg-blob-3: rgba(245, 158, 11, .14);
}

[data-theme="light-amber"] {
  --bg-blob-1: rgba(217, 119, 6, .22);
  --bg-blob-2: rgba(234, 88, 12, .15);
  --bg-blob-3: rgba(236, 72, 153, .12);
}

[data-theme="light-emerald"] {
  --bg-blob-1: rgba(5, 150, 105, .24);
  --bg-blob-2: rgba(13, 148, 136, .16);
  --bg-blob-3: rgba(101, 163, 13, .14);
}

/* ── La capa ─────────────────────────────────────────────────────
 *
 * Las manchas son degradados radiales que se apagan solos (\`transparent\` al
 * 65 %): el difuminado sale del propio degradado, sin \`filter: blur()\`, que en
 * una capa fija a pantalla completa cuesta caro en portátiles.
 *
 * El movimiento anima solo \`transform\`, así que va en la GPU y no repinta.
 * \`inset\` negativo: el desplazamiento no deja ver bordes.
 */

body::before {
  content: '';
  position: fixed;
  inset: -10vmax;
  pointer-events: none;
  z-index: 0;
  background-image:
    radial-gradient(58vmax 52vmax at 14% 10%, var(--bg-blob-1), transparent 72%),
    radial-gradient(52vmax 46vmax at 90% 16%, var(--bg-blob-2), transparent 74%),
    radial-gradient(66vmax 58vmax at 60% 98%, var(--bg-blob-3), transparent 76%);
  background-repeat: no-repeat;
  transform: translate3d(0, 0, 0);
  will-change: transform;
  animation: bgDrift var(--bg-speed, 110s) ease-in-out infinite alternate;
  transition: opacity .5s ease;
}

@keyframes bgDrift {
  0%   { transform: translate3d(0, 0, 0) scale(1); }
  50%  { transform: translate3d(-2.4%, 1.8%, 0) scale(1.06); }
  100% { transform: translate3d(2%, -1.4%, 0) scale(1.03); }
}

/* ── Velocidad configurable ────────────────────────────────────── */

[data-bg-motion="off"] body::before {
  animation: none;
}

[data-bg-motion="soft"] body::before {
  --bg-speed: 110s;
}

[data-bg-motion="lively"] body::before {
  --bg-speed: 42s;
}

/* ── Estilos alternativos ─────────────────────────────────────── */

/* Liso: solo el fondo de la paleta. */
[data-bg="plain"] body::before {
  animation: none;
  opacity: 0;
}

/* Malla: la rejilla de siempre, pero opcional y más tenue. */
[data-bg="mesh"] body::before {
  inset: 0;
  animation: none;
  transform: none;
  background-image:
    linear-gradient(rgba(255, 255, 255, .03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, .03) 1px, transparent 1px);
  background-size: 56px 56px;
}

[data-base="light"][data-bg="mesh"] body::before {
  background-image:
    linear-gradient(rgba(0, 0, 0, .038) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 0, 0, .038) 1px, transparent 1px);
}

/* Quien pide menos movimiento en su sistema manda, aunque tenga "Vivo". */
@media (prefers-reduced-motion: reduce) {
  body::before {
    animation: none !important;
  }
}
`, "",{"version":3,"sources":["webpack://./src/styles/background.css"],"names":[],"mappings":"AAAA;;;;;;;;;;;;;;EAcE;;AAEF,mEAAmE;;AAEnE;;EAEE,oCAAoC;EACpC,oCAAoC;EACpC,oCAAoC;AACtC;;AAEA;EACE,mCAAmC;EACnC,oCAAoC;EACpC,oCAAoC;AACtC;;AAEA;EACE,mCAAmC;EACnC,oCAAoC;EACpC,oCAAoC;AACtC;;AAEA;EACE,oCAAoC;EACpC,oCAAoC;EACpC,oCAAoC;AACtC;;AAEA;EACE,oCAAoC;EACpC,oCAAoC;EACpC,mCAAmC;AACrC;;AAEA;EACE,oCAAoC;EACpC,oCAAoC;EACpC,oCAAoC;AACtC;;AAEA,iFAAiF;;AAEjF;EACE,oCAAoC;EACpC,oCAAoC;EACpC,oCAAoC;AACtC;;AAEA;EACE,mCAAmC;EACnC,oCAAoC;EACpC,mCAAmC;AACrC;;AAEA;EACE,oCAAoC;EACpC,oCAAoC;EACpC,oCAAoC;AACtC;;AAEA;EACE,mCAAmC;EACnC,oCAAoC;EACpC,oCAAoC;AACtC;;AAEA;EACE,mCAAmC;EACnC,mCAAmC;EACnC,oCAAoC;AACtC;;AAEA;EACE,mCAAmC;EACnC,oCAAoC;EACpC,oCAAoC;AACtC;;AAEA;;;;;;;;EAQE;;AAEF;EACE,WAAW;EACX,eAAe;EACf,cAAc;EACd,oBAAoB;EACpB,UAAU;EACV;;;gFAG8E;EAC9E,4BAA4B;EAC5B,+BAA+B;EAC/B,sBAAsB;EACtB,uEAAuE;EACvE,4BAA4B;AAC9B;;AAEA;EACE,OAAO,wCAAwC,EAAE;EACjD,OAAO,kDAAkD,EAAE;EAC3D,OAAO,gDAAgD,EAAE;AAC3D;;AAEA,qEAAqE;;AAErE;EACE,eAAe;AACjB;;AAEA;EACE,gBAAgB;AAClB;;AAEA;EACE,eAAe;AACjB;;AAEA,oEAAoE;;AAEpE,sCAAsC;AACtC;EACE,eAAe;EACf,UAAU;AACZ;;AAEA,6DAA6D;AAC7D;EACE,QAAQ;EACR,eAAe;EACf,eAAe;EACf;;yEAEuE;EACvE,0BAA0B;AAC5B;;AAEA;EACE;;oEAEkE;AACpE;;AAEA,0EAA0E;AAC1E;EACE;IACE,0BAA0B;EAC5B;AACF","sourcesContent":["/**\r\n * Fondo de la aplicación — campo de degradados por paleta.\r\n *\r\n * Antes había una rejilla fija en `body::before` (ver `design-system.css`) que\r\n * se veía en todas las pantallas y competía con el contenido. Ahora el fondo es\r\n * un campo de tres manchas radiales muy difuminadas que cambian de tono con la\r\n * paleta activa (`data-theme`) y, opcionalmente, se mueven despacio.\r\n *\r\n * Lo controla `theme/background.js` escribiendo dos atributos en <html>:\r\n *\r\n *   data-bg        → aurora (por defecto) | mesh (rejilla antigua) | plain\r\n *   data-bg-motion → off | soft (por defecto) | lively\r\n *\r\n * Sin `<html data-bg>` no se pinta nada raro: se asume `aurora`.\r\n */\r\n\r\n/* ── Tonos del campo, uno por paleta ─────────────────────────── */\r\n\r\n:root,\r\n[data-theme=\"dark\"] {\r\n  --bg-blob-1: rgba(99, 102, 241, .42);\r\n  --bg-blob-2: rgba(139, 92, 246, .30);\r\n  --bg-blob-3: rgba(34, 211, 238, .22);\r\n}\r\n\r\n[data-theme=\"midnight\"] {\r\n  --bg-blob-1: rgba(37, 99, 235, .45);\r\n  --bg-blob-2: rgba(99, 102, 241, .30);\r\n  --bg-blob-3: rgba(56, 189, 248, .20);\r\n}\r\n\r\n[data-theme=\"ocean\"] {\r\n  --bg-blob-1: rgba(6, 182, 212, .40);\r\n  --bg-blob-2: rgba(59, 130, 246, .28);\r\n  --bg-blob-3: rgba(16, 185, 129, .22);\r\n}\r\n\r\n[data-theme=\"aurora\"] {\r\n  --bg-blob-1: rgba(16, 185, 129, .42);\r\n  --bg-blob-2: rgba(45, 212, 191, .28);\r\n  --bg-blob-3: rgba(132, 204, 22, .16);\r\n}\r\n\r\n[data-theme=\"dusk\"] {\r\n  --bg-blob-1: rgba(236, 72, 153, .42);\r\n  --bg-blob-2: rgba(168, 85, 247, .30);\r\n  --bg-blob-3: rgba(244, 63, 94, .20);\r\n}\r\n\r\n[data-theme=\"sunset\"] {\r\n  --bg-blob-1: rgba(245, 158, 11, .40);\r\n  --bg-blob-2: rgba(249, 115, 22, .28);\r\n  --bg-blob-3: rgba(236, 72, 153, .18);\r\n}\r\n\r\n/* Paletas claras: el mismo dibujo, mucho más suave para no ensuciar el blanco. */\r\n\r\n[data-theme=\"light\"] {\r\n  --bg-blob-1: rgba(99, 102, 241, .26);\r\n  --bg-blob-2: rgba(139, 92, 246, .18);\r\n  --bg-blob-3: rgba(56, 189, 248, .16);\r\n}\r\n\r\n[data-theme=\"light-sky\"] {\r\n  --bg-blob-1: rgba(2, 132, 199, .24);\r\n  --bg-blob-2: rgba(99, 102, 241, .16);\r\n  --bg-blob-3: rgba(6, 182, 212, .16);\r\n}\r\n\r\n[data-theme=\"light-teal\"] {\r\n  --bg-blob-1: rgba(13, 148, 136, .24);\r\n  --bg-blob-2: rgba(16, 185, 129, .16);\r\n  --bg-blob-3: rgba(14, 165, 233, .14);\r\n}\r\n\r\n[data-theme=\"light-rose\"] {\r\n  --bg-blob-1: rgba(225, 29, 72, .22);\r\n  --bg-blob-2: rgba(236, 72, 153, .16);\r\n  --bg-blob-3: rgba(245, 158, 11, .14);\r\n}\r\n\r\n[data-theme=\"light-amber\"] {\r\n  --bg-blob-1: rgba(217, 119, 6, .22);\r\n  --bg-blob-2: rgba(234, 88, 12, .15);\r\n  --bg-blob-3: rgba(236, 72, 153, .12);\r\n}\r\n\r\n[data-theme=\"light-emerald\"] {\r\n  --bg-blob-1: rgba(5, 150, 105, .24);\r\n  --bg-blob-2: rgba(13, 148, 136, .16);\r\n  --bg-blob-3: rgba(101, 163, 13, .14);\r\n}\r\n\r\n/* ── La capa ─────────────────────────────────────────────────────\r\n *\r\n * Las manchas son degradados radiales que se apagan solos (`transparent` al\r\n * 65 %): el difuminado sale del propio degradado, sin `filter: blur()`, que en\r\n * una capa fija a pantalla completa cuesta caro en portátiles.\r\n *\r\n * El movimiento anima solo `transform`, así que va en la GPU y no repinta.\r\n * `inset` negativo: el desplazamiento no deja ver bordes.\r\n */\r\n\r\nbody::before {\r\n  content: '';\r\n  position: fixed;\r\n  inset: -10vmax;\r\n  pointer-events: none;\r\n  z-index: 0;\r\n  background-image:\r\n    radial-gradient(58vmax 52vmax at 14% 10%, var(--bg-blob-1), transparent 72%),\r\n    radial-gradient(52vmax 46vmax at 90% 16%, var(--bg-blob-2), transparent 74%),\r\n    radial-gradient(66vmax 58vmax at 60% 98%, var(--bg-blob-3), transparent 76%);\r\n  background-repeat: no-repeat;\r\n  transform: translate3d(0, 0, 0);\r\n  will-change: transform;\r\n  animation: bgDrift var(--bg-speed, 110s) ease-in-out infinite alternate;\r\n  transition: opacity .5s ease;\r\n}\r\n\r\n@keyframes bgDrift {\r\n  0%   { transform: translate3d(0, 0, 0) scale(1); }\r\n  50%  { transform: translate3d(-2.4%, 1.8%, 0) scale(1.06); }\r\n  100% { transform: translate3d(2%, -1.4%, 0) scale(1.03); }\r\n}\r\n\r\n/* ── Velocidad configurable ────────────────────────────────────── */\r\n\r\n[data-bg-motion=\"off\"] body::before {\r\n  animation: none;\r\n}\r\n\r\n[data-bg-motion=\"soft\"] body::before {\r\n  --bg-speed: 110s;\r\n}\r\n\r\n[data-bg-motion=\"lively\"] body::before {\r\n  --bg-speed: 42s;\r\n}\r\n\r\n/* ── Estilos alternativos ─────────────────────────────────────── */\r\n\r\n/* Liso: solo el fondo de la paleta. */\r\n[data-bg=\"plain\"] body::before {\r\n  animation: none;\r\n  opacity: 0;\r\n}\r\n\r\n/* Malla: la rejilla de siempre, pero opcional y más tenue. */\r\n[data-bg=\"mesh\"] body::before {\r\n  inset: 0;\r\n  animation: none;\r\n  transform: none;\r\n  background-image:\r\n    linear-gradient(rgba(255, 255, 255, .03) 1px, transparent 1px),\r\n    linear-gradient(90deg, rgba(255, 255, 255, .03) 1px, transparent 1px);\r\n  background-size: 56px 56px;\r\n}\r\n\r\n[data-base=\"light\"][data-bg=\"mesh\"] body::before {\r\n  background-image:\r\n    linear-gradient(rgba(0, 0, 0, .038) 1px, transparent 1px),\r\n    linear-gradient(90deg, rgba(0, 0, 0, .038) 1px, transparent 1px);\r\n}\r\n\r\n/* Quien pide menos movimiento en su sistema manda, aunque tenga \"Vivo\". */\r\n@media (prefers-reduced-motion: reduce) {\r\n  body::before {\r\n    animation: none !important;\r\n  }\r\n}\r\n"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
